import Fuse from 'fuse.js';
import { protocolsData } from '../components/protocols/protocolsData';
import { expandQuery } from './expandAbbreviations';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';

// Build searchable index from all app content
export const buildSearchIndex = async (hospitals) => {
  const searchableItems = [];

  // Index Emergency Protocols
  protocolsData.forEach(protocol => {
    searchableItems.push({
      type: 'protocol',
      id: protocol.title,
      title: protocol.title,
      category: protocol.category,
      searchTerms: protocol.searchTerms || [],
      content: protocol.content ? JSON.stringify(protocol.content) : '',
      breadcrumbs: ['Emergency Protocols', protocol.category],
      navigationPath: { screen: 'emergency', protocol }
    });
  });

  // Index Needlestick Injury Section
  searchableItems.push({
    type: 'section',
    id: 'needlestick',
    title: 'Needlestick Injury',
    category: 'Quick Access',
    searchTerms: ['needlestick', 'needle stick', 'injury', 'exposure', 'blood exposure', 'occupational health', 'IRIS reporter', 'source patient', 'employee blood', 'post-exposure prophylaxis', 'PEP', 'sharps injury'],
    content: 'Hospital-specific protocols for needlestick injuries. Immediate actions: wash wound, flush mucous membranes, report to manager, complete injury report, follow hospital protocol. BSLMC/THI protocol includes IRIS Reporter, source patient blood collection, employee blood collection, occupational health contact.',
    breadcrumbs: ['Quick Access', 'Needlestick Injury'],
    navigationPath: { screen: 'needlestick' }
  });

  // Index Kudos & Concerns Section
  searchableItems.push({
    type: 'section',
    id: 'concerns-integrity',
    title: 'BCM Integrity Hotline',
    category: 'Kudos & Concerns',
    searchTerms: ['integrity hotline', 'report concerns', 'compliance', 'ethics', 'misconduct', 'anonymous reporting', 'office of compliance'],
    content: 'Compliance, ethics, legal or safety violations, or anything to be handled outside the department. BCM Office of Compliance, confidential, anonymous option, 24 hours a day. Call 1-888-294-8970 or report online.',
    breadcrumbs: ['Kudos & Concerns', 'BCM Integrity Hotline'],
    navigationPath: { screen: 'concerns' }
  });

  searchableItems.push({
    type: 'section',
    id: 'concerns-professionalism',
    title: 'Professionalism report',
    category: 'Kudos & Concerns',
    searchTerms: ['professionalism', 'concerns', 'REDCap', 'formal reporting', 'mistreatment', 'site director'],
    content: 'Mistreatment, unprofessional behaviour, or a pattern worth documenting. Goes to the department professionalism process and the site director of that rotation, through REDCap.',
    breadcrumbs: ['Kudos & Concerns', 'Professionalism report'],
    navigationPath: { screen: 'concerns' }
  });

  searchableItems.push({
    type: 'section',
    id: 'concerns-feedback',
    title: 'Quick note or kudos',
    category: 'Kudos & Concerns',
    searchTerms: ['kudos', 'praise', 'feedback', 'hospital feedback', 'faculty feedback', 'trainee feedback', 'anonymous feedback', 'thank you'],
    content: 'Praise or a small concern about a hospital, a faculty member or a trainee. Anonymous unless you add your name.',
    breadcrumbs: ['Kudos & Concerns', 'Quick note or kudos'],
    navigationPath: { screen: 'concerns' }
  });

  // Index BCM Anesthesiology Section
  searchableItems.push({
    type: 'section',
    id: 'bcm-anesthesiology',
    title: 'BCM Anesthesiology',
    category: 'Departmental Resources',
    searchTerms: ['BCM anesthesiology', 'department', 'contacts', 'residency', 'fellowships', 'faculty', 'departmental resources', 'policies'],
    content: 'BCM Anesthesiology department resources including overview, contacts, residency program, fellowships, and faculty information',
    breadcrumbs: ['BCM Anesthesiology'],
    navigationPath: { screen: 'bcm-anesthesiology' }
  });

  // Index BCM Anesthesiology - Contacts
  searchableItems.push({
    type: 'bcm-content',
    id: 'bcm-contacts',
    title: 'BCM Anesthesiology Contacts',
    category: 'BCM Anesthesiology',
    searchTerms: ['contacts', 'contact information', 'email', 'chair', 'leadership', 'program director', 'residency director', 'PD', 'APD', 'associate program director', 'assistant program director', 'site director', 'fellowship director', 'program coordinator', 'Alicia Viney', 'Lee Chang', 'Susan Lee', 'Chris Deng', 'James Anton', 'department chair', 'vice chair', 'chief resident', 'administrative assistant'],
    content: 'Contact information for BCM Anesthesiology department leadership, residency program directors (Dr. Lee Chang, Dr. Susan Lee, Dr. Chris Deng), program coordinator (Alicia Viney), site directors for all rotation sites (Ben Taub, BSLMC, THI, TCH, VA, MD Anderson), fellowship directors (cardiac, regional, pain, critical care, pediatric), and administrative staff.',
    breadcrumbs: ['BCM Anesthesiology', 'Contacts'],
    navigationPath: { screen: 'bcm-anesthesiology' }
  });

  // Index BCM Anesthesiology - Residency
  searchableItems.push({
    type: 'bcm-content',
    id: 'bcm-residency',
    title: 'BCM Anesthesiology Residency',
    category: 'BCM Anesthesiology',
    searchTerms: ['residency', 'residency program', 'training program', 'CA1', 'CA2', 'CA3', 'curriculum', 'schedule', 'rotation schedule', 'call schedule', 'block schedule', 'rotations', 'requirements', 'milestones', 'ACGME', 'evaluations', 'didactics', 'conferences', 'vacation', 'moonlighting', 'match', 'application', 'interview'],
    content: 'BCM Anesthesiology Residency Program information including curriculum, rotation schedules, call schedules, training requirements, ACGME milestones, evaluations, didactic conferences, vacation policies, moonlighting information, and application process.',
    breadcrumbs: ['BCM Anesthesiology', 'Residency'],
    navigationPath: { screen: 'bcm-anesthesiology' }
  });

  // Index BCM Anesthesiology - Fellowships
  searchableItems.push({
    type: 'bcm-content',
    id: 'bcm-fellowships',
    title: 'BCM Anesthesiology Fellowships',
    category: 'BCM Anesthesiology',
    searchTerms: ['fellowship', 'fellowships', 'advanced training', 'subspecialty', 'cardiac anesthesia', 'cardiothoracic', 'regional anesthesia', 'pain management', 'critical care', 'pediatric anesthesia', 'obstetric anesthesia', 'neuroanesthesia', 'fellowship application', 'fellowship director'],
    content: 'BCM Anesthesiology Fellowship Programs including cardiac anesthesia, regional anesthesia, pain management, critical care, pediatric anesthesia, obstetric anesthesia, and neuroanesthesia fellowships. Fellowship directors, application information, and program details.',
    breadcrumbs: ['BCM Anesthesiology', 'Fellowships'],
    navigationPath: { screen: 'bcm-anesthesiology' }
  });

  // Index BCM Anesthesiology - Faculty
  searchableItems.push({
    type: 'bcm-content',
    id: 'bcm-faculty',
    title: 'BCM Anesthesiology Faculty',
    category: 'BCM Anesthesiology',
    searchTerms: ['faculty', 'faculty list', 'attendings', 'staff', 'professors', 'clinical faculty', 'academic faculty', 'faculty directory', 'specialties', 'subspecialty interests'],
    content: 'BCM Anesthesiology faculty directory including faculty names, specialties, subspecialty interests, and contact information for attending anesthesiologists and clinical staff.',
    breadcrumbs: ['BCM Anesthesiology', 'Faculty'],
    navigationPath: { screen: 'bcm-anesthesiology' }
  });

  // Index BCM Resources Section
  searchableItems.push({
    type: 'section',
    id: 'bcm-resources',
    title: 'Baylor College of Medicine Resources',
    category: 'College-Wide Resources',
    searchTerms: ['BCM', 'Baylor College of Medicine', 'college resources', 'GME', 'trainees', 'faculty resources', 'policies'],
    content: 'Baylor College of Medicine college-wide resources and policies for trainees and faculty',
    breadcrumbs: ['BCM Resources'],
    navigationPath: { screen: 'bcm-resources' }
  });

  // Index BCM Resources - GME Trainees
  searchableItems.push({
    type: 'bcm-content',
    id: 'bcm-gme-trainees',
    title: 'BCM GME Trainee Resources',
    category: 'BCM Resources',
    searchTerms: ['GME', 'graduate medical education', 'trainee resources', 'resident resources', 'benefits', 'salary', 'pay', 'stipend', 'insurance', 'health insurance', 'dental', 'vision', 'parking', 'meal card', 'housing', 'leave', 'vacation', 'sick leave', 'parental leave', 'maternity leave', 'paternity leave', 'wellness', 'counseling', 'mental health', 'employee assistance', 'EAP', 'GME office', 'trainee policies', 'duty hours', 'ACGME requirements', 'moonlighting policy', 'licensure', 'DEA', 'credentialing'],
    content: 'BCM GME trainee resources including benefits (health insurance, dental, vision, salary/stipend), leave policies (vacation, sick leave, parental leave, maternity, paternity), wellness resources (counseling, mental health, employee assistance program), parking and meal cards, housing information, GME office contacts, duty hours, ACGME requirements, moonlighting policies, licensure and DEA information, and credentialing.',
    breadcrumbs: ['BCM Resources', 'GME Trainees'],
    navigationPath: { screen: 'bcm-resources' }
  });

  // Index BCM Resources - Faculty
  searchableItems.push({
    type: 'bcm-content',
    id: 'bcm-faculty-resources',
    title: 'BCM Faculty Resources',
    category: 'BCM Resources',
    searchTerms: ['faculty resources', 'faculty benefits', 'faculty policies', 'HR', 'human resources', 'payroll', 'faculty development', 'CME', 'continuing medical education', 'promotion', 'tenure', 'faculty handbook', 'compliance training', 'HIPAA', 'research compliance', 'onboarding', 'new faculty', 'faculty affairs'],
    content: 'BCM faculty resources including benefits, HR and payroll information, faculty development programs, CME opportunities, promotion and tenure guidelines, faculty handbook, compliance training (HIPAA, research compliance), onboarding for new faculty, and faculty affairs contacts.',
    breadcrumbs: ['BCM Resources', 'Faculty'],
    navigationPath: { screen: 'bcm-resources' }
  });

  // Index ACGME Milestones
  searchableItems.push({
    type: 'bcm-content',
    id: 'acgme-milestones',
    title: 'ACGME Milestones',
    category: 'BCM Anesthesiology',
    searchTerms: ['ACGME', 'milestones', 'competencies', 'patient care', 'medical knowledge', 'systems-based practice', 'practice-based learning', 'professionalism', 'interpersonal communication', 'evaluations', 'assessments', 'EPA', 'entrustable professional activities', 'competency based', 'resident evaluation'],
    content: 'ACGME Anesthesiology Milestones including the six core competencies: patient care, medical knowledge, systems-based practice, practice-based learning and improvement, professionalism, and interpersonal and communication skills. Includes milestone levels, EPAs (Entrustable Professional Activities), and competency-based evaluation framework.',
    breadcrumbs: ['BCM Anesthesiology', 'ACGME Milestones'],
    navigationPath: { screen: 'bcm-anesthesiology' }
  });

  // Index Research Hub Section
  searchableItems.push({
    type: 'section',
    id: 'research-hub',
    title: 'Research Hub',
    category: 'Research Resources',
    searchTerms: ['research', 'research hub', 'academic research', 'scholarly activity'],
    content: 'Research resources including getting started guide, active projects, publication tips, and research coordinator contact information',
    breadcrumbs: ['Research Hub'],
    navigationPath: { screen: 'research-hub' }
  });

  // Index Research Hub - Getting Started
  searchableItems.push({
    type: 'research-content',
    id: 'research-getting-started',
    title: 'Getting Started with Research',
    category: 'Research Hub',
    searchTerms: ['getting started', 'research basics', 'first steps', 'IRB', 'institutional review board', 'CITI training', 'research question', 'study design', 'statistician', 'IRB submission', 'IRB approval', 'research mentor', 'faculty mentor', 'QI', 'quality improvement', 'case report', 'retrospective study', 'prospective study', 'power calculation', 'BRAIN portal', 'Office of Clinical Research', 'BCM research resources', 'research funding', 'grants', 'FAER', 'NIH K awards', 'ASA grants'],
    content: 'First steps in research: identify clinical question, find faculty mentor, complete CITI training, develop research question, determine study type (QI, case report, retrospective, prospective), consult statistician, prepare IRB submission. IRB tips: start early (4-8 weeks), be specific and detailed, clearly define risks, include all study materials, know exemptions, plan for amendments. BCM resources: Office of Clinical Research, BRAIN IRB portal, statistician support, CITI training. Funding opportunities: BCM internal grants, FAER grants, NIH K awards, ASA research grants.',
    breadcrumbs: ['Research Hub', 'Getting Started'],
    navigationPath: { screen: 'research-hub' }
  });

  // Index Research Hub - Publication Resources
  searchableItems.push({
    type: 'research-content',
    id: 'research-publications',
    title: 'Publication Resources',
    category: 'Research Hub',
    searchTerms: ['publication', 'publishing', 'manuscript', 'writing tips', 'journals', 'impact factor', 'peer review', 'case reports', 'abstracts', 'poster presentations', 'reference management', 'authorship', 'ICMJE guidelines', 'predatory journals', 'open access', 'citation metrics'],
    content: 'Publication resources for anesthesiology research including writing tips, journal selection, manuscript preparation, authorship guidelines, abstract writing, poster presentation tips, reference management tools, avoiding predatory journals, understanding impact factors and citation metrics.',
    breadcrumbs: ['Research Hub', 'Publication Resources'],
    navigationPath: { screen: 'research-hub' }
  });

  // Index Research Hub - Active Projects
  searchableItems.push({
    type: 'research-content',
    id: 'research-active-projects',
    title: 'Active Research Projects',
    category: 'Research Hub',
    searchTerms: ['active projects', 'ongoing research', 'current studies', 'research opportunities', 'join research', 'collaborate'],
    content: 'Browse active research projects in the BCM Anesthesiology department. View ongoing studies, find collaboration opportunities, and connect with research teams.',
    breadcrumbs: ['Research Hub', 'Active Projects'],
    navigationPath: { screen: 'research-hub' }
  });

  // Index Clinical Tools Section
  searchableItems.push({
    type: 'section',
    id: 'clinical-tools',
    title: 'Clinical Tools',
    category: 'Clinical Resources',
    searchTerms: ['clinical tools', 'point of care', 'decision support', 'reference tools', 'clinical reference'],
    content: 'Point-of-care clinical decision support tools including medication references, preoperative medication guidelines, and anticoagulation guidelines for regional anesthesia',
    breadcrumbs: ['Clinical Tools'],
    navigationPath: { screen: 'clinical-tools' }
  });

  // Index Clinical Tools - Medication Reference
  searchableItems.push({
    type: 'clinical-tool',
    id: 'medication-reference',
    title: 'Medication Reference',
    category: 'Clinical Tools',
    searchTerms: ['medications', 'drugs', 'drug reference', 'medication guide', 'cardiovascular medications', 'CV drugs', 'vasopressors', 'inotropes', 'pressors', 'obstetric medications', 'OB drugs', 'pediatric medications', 'pediatric dosing', 'regional medications', 'local anesthetics', 'epinephrine', 'norepinephrine', 'phenylephrine', 'vasopressin', 'dopamine', 'dobutamine', 'ephedrine', 'milrinone', 'nicardipine', 'nitroprusside', 'nitroglycerin', 'esmolol', 'labetalol', 'oxytocin', 'methergine', 'hemabate', 'lidocaine', 'bupivacaine', 'ropivacaine'],
    content: 'Comprehensive medication reference for cardiovascular drugs (vasopressors, inotropes, antihypertensives), obstetric medications (uterotonics), pediatric dosing, and regional anesthesia medications (local anesthetics). Includes dosing, hemodynamic effects, receptor profiles, pharmacokinetics, and clinical uses.',
    breadcrumbs: ['Clinical Tools', 'Medication Reference'],
    navigationPath: { screen: 'medication-reference' }
  });

  // Index Clinical Tools - Preop Medication Guide
  searchableItems.push({
    type: 'clinical-tool',
    id: 'preop-medication-guide',
    title: 'Preop Medication Guide',
    category: 'Clinical Tools',
    searchTerms: ['preoperative medications', 'preop meds', 'perioperative management', 'GLP-1', 'GLP1', 'semaglutide', 'ozempic', 'wegovy', 'tirzepatide', 'mounjaro', 'liraglutide', 'SGLT2', 'SGLT2i', 'SGLT2 inhibitor', 'empagliflozin', 'dapagliflozin', 'canagliflozin', 'jardiance', 'farxiga', 'invokana', 'preop guidelines', 'medication hold', 'medication continuation', 'diabetes medications', 'perioperative diabetes'],
    content: 'Preoperative medication guidelines including GLP-1 agonist management (semaglutide/Ozempic, tirzepatide/Mounjaro, liraglutide), SGLT2 inhibitor recommendations (empagliflozin/Jardiance, dapagliflozin/Farxiga), and evidence-based perioperative medication continuation and cessation protocols.',
    breadcrumbs: ['Clinical Tools', 'Preop Medication Guide'],
    navigationPath: { screen: 'preop-guide' }
  });

  // Index Clinical Tools - Basic TEE Certification
  searchableItems.push({
    type: 'clinical-tool',
    id: 'tee-certification',
    title: 'Basic TEE Certification',
    category: 'Clinical Tools',
    searchTerms: ['TEE', 'basic TEE', 'PTEeXAM', 'NBE', 'echo boards', 'certification', 'case log', 'TEE log', 'echo log', 'transesophageal', 'echocardiography certification', 'national board of echocardiography', '150 exams', '50 exams'],
    content: 'The NBE supervised training pathway for basic perioperative TEE certification: 150 examinations under supervision within four years, at least 50 personally performed and interpreted, with a case log of examinations studied. Log your examinations and export the log in the NBE format.',
    breadcrumbs: ['Clinical Tools', 'Basic TEE Certification'],
    navigationPath: { screen: 'tee-certification' }
  });

  // Index Clinical Tools - ASRA Anticoagulation
  searchableItems.push({
    type: 'clinical-tool',
    id: 'asra-anticoagulation',
    title: 'ASRA Anticoagulation',
    category: 'Clinical Tools',
    searchTerms: ['ASRA', 'anticoagulation', 'regional anesthesia', 'coags', 'coagulation', 'anticoagulants', 'neuraxial', 'spinal', 'epidural', 'nerve block', 'warfarin', 'coumadin', 'heparin', 'LMWH', 'lovenox', 'enoxaparin', 'fondaparinux', 'DOACs', 'direct oral anticoagulants', 'apixaban', 'rivaroxaban', 'dabigatran', 'edoxaban', 'eliquis', 'xarelto', 'pradaxa', 'savaysa', 'antiplatelet', 'aspirin', 'plavix', 'clopidogrel', 'ticagrelor', 'brilinta', 'timing', 'hold time', 'restart', 'contraindications'],
    content: 'ASRA (American Society of Regional Anesthesia) anticoagulation guidelines for regional anesthesia including neuraxial procedures (spinal, epidural) and peripheral nerve blocks. Covers timing for medication holds, safe resumption after regional procedures, contraindications, and quick reference tables for common anticoagulants including warfarin, heparin, LMWH, DOACs (apixaban, rivaroxaban, dabigatran), and antiplatelet agents.',
    breadcrumbs: ['Clinical Tools', 'ASRA Anticoagulation'],
    navigationPath: { screen: 'asra-coags' }
  });

  // Index Education Center Topics
  const educationTopics = [
    { name: 'Anesthesia Procedures', type: 'procedures', searchTerms: ['procedures', 'techniques', 'how to'] },
    { name: 'Echocardiography / Ultrasound', type: 'echo', searchTerms: ['echo', 'echocardiography', 'ultrasound', 'TEE', 'TTE', 'cardiac ultrasound'] },
    { name: 'Cardiac Anesthesia', type: 'cardiac', searchTerms: ['cardiac', 'heart', 'cardiothoracic', 'CV anesthesia', 'bypass', 'CPB'] },
    { name: 'Pediatric Anesthesia', type: 'pediatric', searchTerms: ['pediatric', 'pediatrics', 'children', 'peds', 'neonatal'] },
    { name: 'Trauma & Critical Care', type: 'trauma', searchTerms: ['trauma', 'critical care', 'ICU', 'resuscitation', 'emergency'] },
    { name: 'Obstetric Anesthesia', type: 'obstetric', searchTerms: ['obstetric', 'OB', 'labor', 'delivery', 'cesarean', 'epidural', 'pregnancy'] },
    { name: 'Regional Anesthesia', type: 'regional', searchTerms: ['regional', 'nerve block', 'blocks', 'peripheral nerve', 'spinal', 'epidural'] },
    { name: 'Neuroanesthesia', type: 'neuro', searchTerms: ['neuro', 'neuroanesthesia', 'brain', 'spine', 'craniotomy'] }
  ];

  educationTopics.forEach(topic => {
    searchableItems.push({
      type: 'education',
      id: topic.type,
      title: topic.name,
      category: 'Education Center',
      searchTerms: topic.searchTerms,
      content: `Education resources for ${topic.name}`,
      breadcrumbs: ['Education Center', topic.name],
      navigationPath: { screen: 'education' }
    });
  });

  // Index Education Content from Firestore
  try {
    const educationSnapshot = await getDocs(collection(db, 'education_content'));
    educationSnapshot.forEach((doc) => {
      const content = doc.data();

      // Build search terms from tags, title, description
      const searchTerms = [
        content.category,
        content.subcategory,
        content.fileType,
        ...(content.tags || []),
        content.trainingLevel,
        content.series
      ].filter(Boolean);

      searchableItems.push({
        type: 'education-content',
        id: doc.id,
        title: content.title,
        category: content.category,
        searchTerms: searchTerms,
        content: `${content.description || ''} ${content.subcategory || ''} ${content.fileType || ''}`,
        breadcrumbs: ['Education Center', content.category, content.title],
        navigationPath: {
          screen: 'education',
          category: content.category,
          contentId: doc.id,
          subcategory: content.subcategory
        },
        metadata: {
          fileType: content.fileType,
          trainingLevel: content.trainingLevel,
          subcategory: content.subcategory,
          downloadURL: content.downloadURL
        }
      });
    });
  } catch (error) {
    console.error('Error indexing education content:', error);
  }

  // Index Hospitals with more detail
  hospitals.forEach(hospital => {
    searchableItems.push({
      type: 'hospital',
      id: hospital.id,
      title: hospital.name,
      category: 'Hospital',
      searchTerms: [hospital.name, hospital.shortName || '', hospital.id],
      content: `Rotation resources for ${hospital.name}`,
      breadcrumbs: ['Hospital Rotations', hospital.name],
      navigationPath: { screen: 'hospital', hospital }
    });
  });

  // Index specific hospital rotations and special content
  // THI - Out of OR Procedures
  searchableItems.push({
    type: 'hospital-content',
    id: 'thi-ooor',
    title: 'Documenting Out-of-OR Procedures',
    category: 'Texas Heart Institute',
    searchTerms: ['OOOR', 'out of OR', 'out of operating room', 'documenting procedures', 'Epic documentation', 'intubation documentation', 'line placement', 'ICU procedures', 'cath lab', 'procedure note', 'billing sheet'],
    content: 'How to document out-of-OR procedures at THI including intubations, line placements, codes. Important: never use Cath Lab Status Board. Use Patient Station or Patient Lists. Complete anesthetic record, procedure note, and billing sheet.',
    breadcrumbs: ['Texas Heart Institute', 'CVOR', 'Out-of-OR Procedures'],
    navigationPath: { screen: 'hospital', hospital: hospitals.find(h => h.id === 'texas-heart'), action: 'showOOOR' }
  });

  // THI - Preop Checklist
  searchableItems.push({
    type: 'hospital-content',
    id: 'thi-preop',
    title: 'Preoperative Checklist',
    category: 'Texas Heart Institute',
    searchTerms: ['preop', 'preoperative', 'checklist', 'THI checklist', 'cardiac surgery checklist'],
    content: 'Preoperative checklist for cardiac surgery cases at Texas Heart Institute',
    breadcrumbs: ['Texas Heart Institute', 'CVOR', 'Preop Checklist'],
    navigationPath: { screen: 'hospital', hospital: hospitals.find(h => h.id === 'texas-heart'), action: 'showPreopChecklist' }
  });

  // THI - Case Setup Guide
  searchableItems.push({
    type: 'hospital-content',
    id: 'thi-case-setup',
    title: 'Case Setup Guide',
    category: 'Texas Heart Institute',
    searchTerms: [
      'case setup', 'drug setup', 'cardiac case setup', 'cardiac drugs', 'ERACS', 'enhanced recovery',
      'CPB', 'cardiopulmonary bypass', 'sternotomy', 'robotic', 'minimally invasive',
      'CABG', 'robotic CABG', 'LIMA-LAD',
      'AATA', 'ascending aortic', 'aortic arch', 'arch repair',
      'TAAA', 'thoracoabdominal', 'aortic aneurysm',
      'TEVAR', 'endovascular', 'zone 0',
      'AAA', 'abdominal aortic aneurysm',
      'heart transplant', 'lung transplant', 'liver transplant', 'kidney transplant',
      'convergent procedure', 'AFib ablation', 'LAA ligation',
      'laser lead extraction',
      'EP cases', 'electrophysiology', 'VT ablation', 'PVC ablation', 'cath lab',
      'TAVR', 'transcatheter aortic valve',
      'heparin', 'TXA', 'amicar', 'anti-fibrinolytic', 'epinephrine', 'norepinephrine',
      'dexmedetomidine', 'ropivacaine', 'ketamine', 'parasternal nerve block',
      'ESP block', 'erector spinae',
      'PA catheter', 'companion catheter', 'arterial line', 'MAC', 'TEE', 'transesophageal echo',
      'immunosuppression', 'methylprednisolone', 'basiliximab',
      'nicardipine', 'mannitol', 'vasopressin',
      'cardiologist preferences', 'attending preferences'
    ],
    content: 'Comprehensive cardiac case setup guide for THI CVOR. Includes drug setup (syringe medications, drips, emergency drugs, uppers and downers), ERACS drugs (dexmedetomidine, ropivacaine, ketamine for multimodal analgesia), and procedure-specific guidelines for: CPB cases (sternotomy and robotic/minimally invasive), robotic CABG (LIMA-LAD), AATA (ascending aortic and aortic arch repair), TAAA (thoracoabdominal aortic aneurysm), TEVAR, zone 0 TEVAR, AAA repair, heart/lung/liver/kidney transplants, convergent procedure (epicardial AFib ablation + LAA ligation), laser lead extraction, EP cases (AFib ablation, VT ablation, PVC ablation), and TAVR. Each procedure includes anesthetic type, airway management, monitors, vascular access, additional medications, and special considerations. Covers PA catheters, arterial lines, TEE, immunosuppression protocols, nerve blocks, and cardiologist preferences.',
    breadcrumbs: ['Texas Heart Institute', 'CVOR', 'Case Setup Guide'],
    navigationPath: { screen: 'hospital', hospital: hospitals.find(h => h.id === 'texas-heart'), action: 'showCaseSetupGuide' }
  });

  // Ben Taub - Major rotations
  searchableItems.push({
    type: 'hospital-content',
    id: 'ben-taub-trauma',
    title: 'Ben Taub OR / Trauma',
    category: 'Ben Taub',
    searchTerms: ['trauma', 'ben taub', 'emergency surgery', 'trauma anesthesia'],
    content: 'OR and trauma anesthesia rotation at Ben Taub General Hospital',
    breadcrumbs: ['Ben Taub', 'OR / Trauma'],
    navigationPath: { screen: 'hospital', hospital: hospitals.find(h => h.id === 'ben-taub') }
  });

  searchableItems.push({
    type: 'hospital-content',
    id: 'ben-taub-ob',
    title: 'Ben Taub OB',
    category: 'Ben Taub',
    searchTerms: ['OB', 'obstetric', 'labor and delivery', 'ben taub', 'cesarean section'],
    content: 'Obstetric anesthesia rotation at Ben Taub General Hospital',
    breadcrumbs: ['Ben Taub', 'OB'],
    navigationPath: { screen: 'hospital', hospital: hospitals.find(h => h.id === 'ben-taub') }
  });

  // BSLMC - Neuro
  searchableItems.push({
    type: 'hospital-content',
    id: 'bslmc-neuro',
    title: 'BSLMC Neuroanesthesia',
    category: 'Baylor St. Luke\'s',
    searchTerms: ['neuro', 'neuroanesthesia', 'BSLMC', 'brain surgery', 'craniotomy'],
    content: 'Neuroanesthesia rotation at Baylor St. Luke\'s Medical Center',
    breadcrumbs: ['Baylor St. Luke\'s', 'Neuro'],
    navigationPath: { screen: 'hospital', hospital: hospitals.find(h => h.id === 'st-lukes') }
  });

  // TCH - Pediatric rotations
  searchableItems.push({
    type: 'hospital-content',
    id: 'tch-or',
    title: 'Texas Children\'s Hospital OR',
    category: 'Texas Children\'s',
    searchTerms: ['TCH', 'pediatric', 'children', 'pediatric anesthesia', 'texas childrens'],
    content: 'General pediatric anesthesia rotation at Texas Children\'s Hospital',
    breadcrumbs: ['Texas Children\'s', 'OR'],
    navigationPath: { screen: 'hospital', hospital: hospitals.find(h => h.id === 'texas-childrens') }
  });

  searchableItems.push({
    type: 'hospital-content',
    id: 'tch-cardiac',
    title: 'Texas Children\'s Cardiac',
    category: 'Texas Children\'s',
    searchTerms: ['pediatric cardiac', 'congenital heart', 'TCH cardiac', 'pediatric heart surgery'],
    content: 'Pediatric cardiac anesthesia rotation at Texas Children\'s Hospital',
    breadcrumbs: ['Texas Children\'s', 'Cardiac'],
    navigationPath: { screen: 'hospital', hospital: hospitals.find(h => h.id === 'texas-childrens') }
  });

  // VA - Rotations
  searchableItems.push({
    type: 'hospital-content',
    id: 'va-pain',
    title: 'VA Pain Management',
    category: 'VA Medical Center',
    searchTerms: ['pain', 'chronic pain', 'pain management', 'VA', 'interventional pain'],
    content: 'Pain management rotation at Michael E. DeBakey VA Medical Center',
    breadcrumbs: ['VA Medical Center', 'Pain'],
    navigationPath: { screen: 'hospital', hospital: hospitals.find(h => h.id === 'va') }
  });

  // MD Anderson - Rotations
  searchableItems.push({
    type: 'hospital-content',
    id: 'mda-icu',
    title: 'MD Anderson ICU',
    category: 'MD Anderson',
    searchTerms: ['ICU', 'critical care', 'MD Anderson', 'oncology ICU', 'cancer'],
    content: 'Critical care rotation at MD Anderson Cancer Center',
    breadcrumbs: ['MD Anderson', 'ICU'],
    navigationPath: { screen: 'hospital', hospital: hospitals.find(h => h.id === 'md-anderson') }
  });

  searchableItems.push({
    type: 'hospital-content',
    id: 'mda-acute-pain',
    title: 'MD Anderson Acute Pain',
    category: 'MD Anderson',
    searchTerms: ['acute pain', 'pain service', 'MD Anderson', 'cancer pain', 'oncology pain'],
    content: 'Acute pain service rotation at MD Anderson Cancer Center',
    breadcrumbs: ['MD Anderson', 'Acute Pain'],
    navigationPath: { screen: 'hospital', hospital: hospitals.find(h => h.id === 'md-anderson') }
  });

  return searchableItems;
};

// Configure Fuse.js for fuzzy search
export const createSearchEngine = (searchableItems) => {
  return new Fuse(searchableItems, {
    keys: [
      { name: 'title', weight: 3 },           // Title most important
      { name: 'searchTerms', weight: 2 },     // Search terms high priority
      { name: 'category', weight: 1.5 },      // Category important
      { name: 'content', weight: 0.5 }        // Content lower priority
    ],
    threshold: 0.2,                           // Stricter matching to reduce extraneous results
    includeScore: true,                       // Include match scores
    includeMatches: true,                     // Include match positions for highlighting
    minMatchCharLength: 2,                    // Minimum 2 characters to match
    ignoreLocation: true,                     // Search entire string
    findAllMatches: true                      // Find all matching patterns
  });
};

// Perform search with "Did you mean" suggestions and abbreviation expansion
export const performSearch = (searchEngine, query, searchableItems) => {
  if (!query || query.length < 2) {
    return { results: [], suggestions: [] };
  }

  // Expand abbreviations
  const expandedQueries = expandQuery(query);

  // Search for all expanded terms
  const allResults = new Map(); // Use Map to deduplicate by ID

  expandedQueries.forEach(expandedQuery => {
    const searchResults = searchEngine.search(expandedQuery);
    searchResults.forEach(result => {
      const item = result.item;
      const itemKey = `${item.type}-${item.id}`;

      // Keep result with best score
      if (!allResults.has(itemKey) || allResults.get(itemKey).score > result.score) {
        allResults.set(itemKey, { ...result, item });
      }
    });
  });

  // Convert Map to array and sort by score
  const combinedResults = Array.from(allResults.values())
    .sort((a, b) => a.score - b.score)
    .map(result => result.item);

  // If no results, try to find suggestions with more lenient threshold
  let suggestions = [];
  if (combinedResults.length === 0) {
    const lenientEngine = new Fuse(searchableItems, {
      keys: ['title', 'searchTerms'],
      threshold: 0.5,  // More lenient for suggestions
      includeScore: true
    });

    const lenientResults = lenientEngine.search(query);
    suggestions = lenientResults
      .slice(0, 3)
      .map(result => result.item.title);
  }

  return {
    results: combinedResults,
    suggestions,
    query
  };
};

// Highlight matching text in search results
export const highlightMatches = (text, matches) => {
  if (!matches || matches.length === 0) return text;

  // This is a placeholder - we'll implement text highlighting later
  return text;
};
